import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Avatar,
    Snackbar,
    InputAdornment,
    Grid,
    Divider,
    Chip,
    Switch,
    FormControlLabel
} from '@mui/material';
import {
    Add,
    Edit,
    Category as CategoryIcon,
    Search as SearchIcon,
    Layers,
    Storefront,
    BarChart,
    Link as LinkIcon,
    PhotoCamera,
    Close as CloseIcon // Change this line
} from '@mui/icons-material';

import LinearProgress from '@mui/material/LinearProgress';

import { useQueryClient } from '@tanstack/react-query';
import { useAdminAllMarketCategories, useCreateMarketCategory, useUpdateMarketCategory } from 'hooks/useMarketCategory';
import PermissionGuard from 'ui-component/PermissionGuard';
import fileService from 'api/fileService'; //

export default function MarketCategoryList() {
    const queryClient = useQueryClient();

    // --- State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // --- File Upload State ---
    const [isUploading, setIsUploading] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState(''); // Stores Cloudinary URL
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isActive, setIsActive] = useState(true);

    // --- Data Hooks ---
    const { data: categoriesRes, isLoading } = useAdminAllMarketCategories();
    const categories = categoriesRes?.data || [];
    const totalCount = categoriesRes?.total || 0;

    const filteredCategories = categories.filter((cat) => cat.name.toLowerCase().includes(searchTerm.toLowerCase()));

    // --- Mutation Hooks ---
    const createMutation = useCreateMarketCategory({
        onSuccess: () => handleSuccess('Category created!'),
        onError: (err) => handleError(err)
    });

    const updateMutation = useUpdateMarketCategory({
        onSuccess: () => handleSuccess('Category updated!'),
        onError: (err) => handleError(err)
    });

    // --- Handlers ---
    const handleSuccess = (msg) => {
        setSnackbar({ open: true, message: msg, severity: 'success' });
        queryClient.invalidateQueries({ queryKey: ['adminAllCategories'] });
        handleCloseModal();
    };

    const handleError = (err) => {
        setSnackbar({ open: true, message: err?.response?.data?.message || 'Action failed', severity: 'error' });
    };

    const handleOpenModal = (category = null) => {
        if (category) {
            setSelectedCategory(category);
            setThumbnailUrl(category.thumbnail); // Set existing image if editing
            setIsActive(category.active !== false); // Default to true if undefined
        } else {
            setSelectedCategory(null);
            setThumbnailUrl('');
            setIsActive(true);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedCategory(null);
        setThumbnailUrl('');
        setUploadProgress(0);
    };

    // ✅ Handle File Selection and Upload
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setIsUploading(true);
            // Pass setUploadProgress as the second argument here!
            const uploadedFile = await fileService.uploadFile(file, (progress) => {
                setUploadProgress(progress);
            });

            setThumbnailUrl(typeof uploadedFile === 'string' ? uploadedFile : uploadedFile.url);
            setIsUploading(false);
        } catch (error) {
            console.error('Upload failed', error);
            setSnackbar({ open: true, message: 'Image upload failed', severity: 'error' });
            setIsUploading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const payload = {
            name: formData.get('name'),
            description: formData.get('description'),
            thumbnail: thumbnailUrl, // ✅ Send the Cloudinary URL
            active: isActive
        };

        if (selectedCategory) {
            updateMutation.mutate({ id: selectedCategory.id, ...payload });
        } else {
            createMutation.mutate(payload);
        }
    };

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            {/* Header and KPI Cards remain the same as previous version */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Market Categories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure store types and platform segments
                    </Typography>
                </Box>
                <PermissionGuard permission="manage_categories" action="create">
                    <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()} sx={{ borderRadius: 2 }}>
                        Add New Category
                    </Button>
                </PermissionGuard>
            </Stack>

            <Grid container spacing={3} mb={4}>
                <CategoryStatCard title="Total Categories" value={totalCount} icon={<Layers color="primary" />} color="#1976d2" />
                {/* <CategoryStatCard title="Active Vendors" value="--" icon={<Storefront color="success" />} color="#2e7d32" /> */}
            </Grid>

            {/* Table */}
            <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <Box p={3}>
                    <TextField
                        placeholder="Search categories..."
                        size="small"
                        fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ maxWidth: 400 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, pl: 3 }}>THUMBNAIL</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>NAME</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>DESCRIPTION</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>STATUS</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, pr: 3 }}>
                                    ACTIONS
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" py={5}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCategories.map((cat) => (
                                    <TableRow key={cat.id} hover>
                                        <TableCell sx={{ pl: 3 }}>
                                            <Avatar
                                                src={cat.thumbnail}
                                                variant="rounded"
                                                sx={{ width: 45, height: 45, bgcolor: '#f0f0f0' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {cat.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell color="text.secondary">{cat.description}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={cat.active !== false ? 'Active' : 'Disabled'}
                                                color={cat.active !== false ? 'success' : 'error'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontWeight: 700, borderRadius: 1 }}
                                            />
                                        </TableCell>
                                         <TableCell align="right" sx={{ pr: 3 }}>
                                             <PermissionGuard permission="manage_categories" action="edit">
                                                 <IconButton size="small" onClick={() => handleOpenModal(cat)} sx={{ border: '1px solid #eee' }}>
                                                     <Edit fontSize="small" color="primary" />
                                                 </IconButton>
                                             </PermissionGuard>
                                         </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Modal with File Upload Logic */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                fullWidth
                maxWidth="xs"
                PaperProps={{ component: 'form', onSubmit: handleSubmit, sx: { borderRadius: 3 } }}
            >
                <DialogTitle fontWeight={700}>{selectedCategory ? 'Edit' : 'Create'} Category</DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={3} mt={1}>
                        {/* Image Preview & Upload Button */}
                        <Box sx={{ textAlign: 'center' }}>
                            {thumbnailUrl ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    <Avatar
                                        src={thumbnailUrl}
                                        sx={{ width: 100, height: 100, borderRadius: 2, mx: 'auto', border: '2px solid #eee' }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => setThumbnailUrl('')}
                                        sx={{
                                            position: 'absolute',
                                            top: -10,
                                            right: -10,
                                            bgcolor: 'error.main',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'error.dark' }
                                        }}
                                    >
                                        <CloseIcon fontSize="inherit" /> {/* Change <Close /> to <CloseIcon /> */}
                                    </IconButton>
                                </Box>
                            ) : (
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ width: 100, height: 100, borderRadius: 2, borderStyle: 'dashed', flexDirection: 'column' }}
                                    disabled={isUploading}
                                >
                                    {isUploading ? <CircularProgress size={24} /> : <PhotoCamera fontSize="large" />}
                                    <Typography variant="caption" mt={1}>
                                        Upload
                                    </Typography>
                                    <input hidden accept="image/*" type="file" onChange={handleFileUpload} />
                                </Button>
                            )}
                            {isUploading && (
                                <Box sx={{ width: '100%', mt: 1 }}>
                                    <LinearProgress variant="determinate" value={uploadProgress} />
                                </Box>
                            )}
                        </Box>

                        <TextField label="Category Name" name="name" fullWidth required defaultValue={selectedCategory?.name || ''} />
                        <TextField
                            label="Description"
                            name="description"
                            fullWidth
                            multiline
                            rows={2}
                            defaultValue={selectedCategory?.description || ''}
                        />

                        <FormControlLabel
                            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
                            label={
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        Active Status
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Enable or disable this category for users
                                    </Typography>
                                </Box>
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isUploading || createMutation.isPending || updateMutation.isPending}
                        startIcon={(createMutation.isPending || updateMutation.isPending) && <CircularProgress size={16} color="inherit" />}
                    >
                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Category'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

function CategoryStatCard({ title, value, icon, color }) {
    return (
        <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2.5, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {title.toUpperCase()}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                        {value}
                    </Typography>
                </Box>
                <Avatar sx={{ bgcolor: `${color}15`, p: 1 }}>{icon}</Avatar>
            </Paper>
        </Grid>
    );
}
